import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { Modal } from '../../components/Modal';
import { QrImage } from '../../components/QrImage';
import { Table } from '../../components/Table';
import { newIdempotencyKey } from '../../lib/api';
import { downloadAuthFile } from '../../lib/authImage';
import { useSession } from '../../lib/useSession';
import { QrTimeline } from '../audit/QrTimeline';
import { KitModal } from '../lots/KitModal';
import { describeStyle } from '../style/presets';
import { EditDestinationModal } from './EditDestinationModal';
import { describeApiError, errorCode } from './errors';
import { queryString, useApiQuery } from './hooks';
import { LifecycleModal, type LifecycleAction } from './LifecycleModals';
import { NewVersionForm } from './NewVersionForm';
import { permissionsFor } from './permissions';
import { formatDate, formatNumber, stateChip, typeLabel } from './states';
import type { DestinationChange, NewVersionBody, QrDetail, QrVersion, VersionsResponse } from './types';
import { useOrgApi } from './useOrgApi';
import { QrDownloadActions } from './QrDownloadActions';
import { partnerQrImagePath, shortLink } from './utils';
import { VersionsGrid } from './VersionsGrid';

const DEST_STATUS: Record<string, string> = { ok: 'Sin marcas de riesgo', pending: 'Revisión pendiente (el verificador no respondió)', flagged: 'Marcado como peligroso' };

function actorName(h: DestinationChange): string {
  const by = h.by;
  if (!by) return '—';
  const who = by.name ?? by.email ?? by.id ?? '—';
  if (by.type === 'api_key') return `${who} (API)`;
  if (h.action === 'activate') return `${who} (activación)`;
  return who;
}

/**
 * Versiones del código (R1, R2): grilla de tarjetas, pedido de una nueva y el aviso de cuota cuando
 * ya no quedan regeneraciones gratis.
 */
function VersionsPanel({ qr, canManage }: { qr: QrDetail; canManage: boolean }) {
  const client = useOrgApi();
  const code = qr.code;
  const domainQuery = queryString({ domain: qr.domain !== 'q-r.cl' ? qr.domain : undefined });
  const query = useApiQuery((signal) => client.get<VersionsResponse>(`/v1/qrs/${encodeURIComponent(code)}/versions${domainQuery}`, { signal }), [client, code, domainQuery]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [pending, setPending] = useState<NewVersionBody | null>(null);

  if (query.error) {
    return (
      <Banner tone="error" action={<Button size="sm" variant="secondary" onClick={query.reload}>Reintentar</Button>}>
        {describeApiError(query.error, 'No pudimos cargar las versiones de este QR.')}
      </Banner>
    );
  }
  if (query.loading || !query.data) {
    return <div role="status" className="text-sm text-ink-muted">Cargando versiones…</div>;
  }

  const versions = query.data.items ?? [];
  const freeRegens = query.data.freeRegens ?? null;
  const artSources = versions.filter((v) => v.kind === 'art' && !v.retiredAt && v.prompt);
  const chosen = sourceId ?? artSources.find((v) => v.primary)?.id ?? artSources[0]?.id ?? 'new-prompt';

  async function create(body: NewVersionBody) {
    setGenerating(true);
    setFormError(null);
    setNotice(null);
    try {
      await client.post(`/v1/qrs/${encodeURIComponent(code)}/versions${domainQuery}`, body, { idempotencyKey: newIdempotencyKey() });
      setPending(null);
      setNotice('Pedimos la versión nueva. Aparece en la grilla apenas la imagen esté lista.');
      query.reload();
    } catch (err) {
      if (errorCode(err) === 'confirm_required') {
        setPending(body);
        return;
      }
      setFormError(describeApiError(err, 'No pudimos pedir la versión nueva.'));
    } finally {
      setGenerating(false);
    }
  }

  function submitNew(body: NewVersionBody) {
    // El prompt de una versión que no es la principal viaja explícito: el backend solo reusa el de la principal.
    const source = versions.find((v) => v.id === chosen);
    if (body.source === 'same-prompt' && source && !source.primary && source.prompt) {
      void create({ ...body, source: 'new-prompt', prompt: source.prompt });
      return;
    }
    void create(body);
  }

  async function run(version: QrVersion, action: () => Promise<unknown>, message: string, fallback: string) {
    setBusyId(version.id);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(message);
      query.reload();
    } catch (err) {
      setError(describeApiError(err, fallback));
    } finally {
      setBusyId(null);
    }
  }

  function download(version: QrVersion) {
    if (version.imageUrl) {
      const a = document.createElement('a');
      a.href = version.imageUrl;
      a.download = `${code}-${version.id}.webp`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }
    void downloadAuthFile(partnerQrImagePath(qr, { size: 1024, download: true }), `${code}-1024.png`, { orgId: client.orgId });
  }

  return (
    <div className="space-y-3">
      <div aria-live="polite">{notice && <Banner tone="success">{notice}</Banner>}</div>
      {error && <Banner tone="error">{error}</Banner>}
      <VersionsGrid
        code={code}
        domain={qr.domain}
        versions={versions}
        orgId={client.orgId}
        freeRegens={freeRegens}
        busyId={busyId}
        canManage={canManage}
        onDownload={download}
        onPrimary={(v) =>
          void run(
            v,
            () => client.post(`/v1/qrs/${encodeURIComponent(code)}/versions/${encodeURIComponent(v.id)}/primary${domainQuery}`),
            'Listo: esa versión es la principal. El link del código no cambió.',
            'No pudimos marcar esa versión como principal.',
          )
        }
        onRetire={(v) =>
          void run(
            v,
            () => client.del(`/v1/qrs/${encodeURIComponent(code)}/versions/${encodeURIComponent(v.id)}${domainQuery}`),
            'Versión retirada. Se conserva en la auditoría del código.',
            'No pudimos retirar esa versión.',
          )
        }
        onReuse={(v) => setSourceId(v.id)}
        newVersionForm={
          canManage ? (
            <NewVersionForm
              sources={artSources}
              freeRegens={freeRegens}
              sourceId={chosen}
              onSourceChange={setSourceId}
              generating={generating}
              error={formError}
              onSubmit={submitNew}
            />
          ) : undefined
        }
      />

      {pending && (
        <Modal
          open
          size="sm"
          title="Esta versión cuenta como 1 QR Art de tu plan"
          onClose={() => setPending(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPending(null)}>Cancelar</Button>
              <Button loading={generating} onClick={() => void create({ ...pending, confirm: true })}>Generar y descontar</Button>
            </>
          }
        >
          <p className="text-sm text-gray-700">
            Ya usaste {freeRegens ? `las ${freeRegens.limit}` : 'todas las'} regeneraciones gratis de <span className="font-mono">{code}</span>. Esta
            versión nueva se descuenta de tu cuota de QR Art.
          </p>
          <p className="rounded-md bg-mist px-3 py-2 text-xs text-ink-muted">
            Las versiones anteriores se conservan: puedes volver a cualquiera sin gastar cuota.
          </p>
        </Modal>
      )}
      {freeRegens && freeRegens.used >= freeRegens.limit && (
        <p className="text-xs text-ink-muted">Ya no quedan regeneraciones gratis de este código: las próximas se descuentan de tu cuota de QR Art.</p>
      )}
    </div>
  );
}

export function QrDetailPage() {
  const { code = '' } = useParams();
  const [params] = useSearchParams();
  const domain = params.get('domain') ?? undefined;
  const client = useOrgApi();
  const { activeOrg } = useSession();
  const perms = permissionsFor(activeOrg?.role);
  const query = useApiQuery((signal) => client.get<QrDetail>(`/v1/qrs/${encodeURIComponent(code)}${queryString({ domain })}`, { signal }), [client, code, domain]);
  const [override, setOverride] = useState<Partial<QrDetail> | null>(null);
  const [modal, setModal] = useState<LifecycleAction | 'destination' | 'kit' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (query.error) {
    return (
      <section className="space-y-4">
        <Link to="/consola/qr" className="text-sm text-green underline">← QR activados</Link>
        <Banner tone="error" action={<Button size="sm" variant="secondary" onClick={query.reload}>Reintentar</Button>}>
          {describeApiError(query.error, 'No pudimos cargar la ficha del QR.')}
        </Banner>
      </section>
    );
  }
  if (query.loading || !query.data) {
    return <div role="status" className="p-2 text-sm text-ink-muted">Cargando ficha…</div>;
  }

  const qr: QrDetail = { ...query.data, ...(override ?? {}) };
  const chip = stateChip(qr);
  const allowed = new Set(qr.allowedActions ?? []);
  const can = (a: string) => (qr.allowedActions ? allowed.has(a) : true);
  const isArt = typeLabel(qr) === 'QR Art';
  const generating = qr.artStatus === 'generando';
  const retired = qr.state === 'dado_de_baja';
  const history = [...(qr.history ?? [])].reverse();

  function done(message: string) {
    return (next: Partial<QrDetail>) => {
      setOverride((prev) => ({ ...(prev ?? {}), ...next }));
      setNotice(message);
      query.reload();
    };
  }

  return (
    <section className="space-y-5">
      <Link to="/consola/qr" className="text-sm text-green underline hover:text-ink">← QR activados</Link>
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="font-mono text-2xl font-semibold tracking-wider text-ink">{qr.code}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
            <Chip tone={chip.tone}>{chip.label}</Chip>
            {isArt ? <Chip tone="art" dot={false}>QR Art{qr.artTheme ? ` · ${qr.artTheme}` : ''}</Chip> : <span>{typeLabel(qr)}</span>}
            {qr.lotId && (
              <>
                · <Link to={`/consola/lotes/${encodeURIComponent(qr.lotId)}`} className="font-mono text-green underline">{qr.lotId}</Link>
              </>
            )}
            {qr.domain !== 'q-r.cl' && <span>· {qr.domain}</span>}
          </div>
        </div>
      </div>

      {notice && <Banner tone="success" action={<button type="button" className="text-xs underline" onClick={() => setNotice(null)}>Cerrar</button>}>{notice}</Banner>}
      {qr.suspension?.security && <Banner tone="error">Bloqueado por seguridad por el operador de q-r.cl. Quien escanee ve una advertencia y no llega al destino.</Banner>}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3 rounded-lg border border-line bg-white p-4">
          <div aria-busy={generating}>
            {generating ? (
              <div className="aspect-square w-full max-w-[18rem] animate-pulse rounded-md bg-green-soft motion-reduce:animate-none" role="status" aria-label="Generando QR Art" />
            ) : qr.hasFallback ? (
              <div className="grid grid-cols-[1.4fr,1fr] items-end gap-3">
                <figure className="space-y-1">
                  <QrImage path={partnerQrImagePath(qr, { size: 1024 })} orgId={client.orgId} alt={`QR Art de ${qr.code}`} />
                  <figcaption className="flex items-center gap-2 text-xs text-ink-muted"><Chip tone="error">No escanea</Chip> Arte generado</figcaption>
                </figure>
                <figure className="space-y-1">
                  <QrImage path={partnerQrImagePath(qr, { variant: 'fallback', size: 512 })} orgId={client.orgId} alt={`QR de respaldo entregado para ${qr.code}`} />
                  <figcaption className="flex items-center gap-2 text-xs text-ink-muted"><Chip tone="success">Entregado</Chip> QR de respaldo</figcaption>
                </figure>
              </div>
            ) : (
              <QrImage className="max-w-[18rem]" path={partnerQrImagePath(qr, { size: 1024 })} orgId={client.orgId} alt={`QR de ${qr.code}`} />
            )}
          </div>
          <div className="text-xs text-ink-muted">
            Codifica <span className="font-mono">{qr.qrContent ?? `HTTPS://${qr.domain.toUpperCase()}/${qr.code}`}</span>
          </div>
          {generating && <p className="text-xs text-ink-muted" aria-live="polite">Generando la imagen; mientras tanto el QR muestra la página del partner.</p>}
          {!generating && (
            <QrDownloadActions
              code={qr.code}
              link={shortLink(qr)}
              targetUrl={qr.targetUrl}
              isArt={isArt}
              imagePath={(params) => partnerQrImagePath(qr, params)}
              orgId={client.orgId}
              idPrefix={`qr-${qr.code}`}
            />
          )}
          {perms.manageLots && !retired && (
            <Button size="sm" variant="secondary" onClick={() => setModal('kit')}>Kit de este QR</Button>
          )}
        </div>

        <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 rounded-lg border border-line bg-white p-4 text-sm">
          <dt className="text-xs text-ink-muted">Negocio</dt>
          <dd>{qr.business?.name ?? '—'}</dd>
          <dt className="text-xs text-ink-muted">Referencia externa</dt>
          <dd className="font-mono">{qr.business?.externalRef ?? '—'}</dd>
          <dt className="text-xs text-ink-muted">Destino</dt>
          <dd className="break-all">{qr.targetUrl ?? '—'}</dd>
          <dt className="text-xs text-ink-muted">Revisión anti-phishing</dt>
          <dd>{qr.destStatus ? DEST_STATUS[qr.destStatus] ?? qr.destStatus : '—'}</dd>
          <dt className="text-xs text-ink-muted">Escaneos</dt>
          <dd className="tabular-nums">{formatNumber(qr.scans30d ?? qr.hits)}</dd>
          <dt className="text-xs text-ink-muted">Estilo</dt>
          <dd>
            {isArt ? 'QR Art' : describeStyle(qr.qrStyle)}
            {qr.styleResult?.degraded && <span className="ml-2 text-xs text-warning-text">(ajustado para que se lea)</span>}
          </dd>
          {qr.suspension?.reason && (
            <>
              <dt className="text-xs text-ink-muted">Motivo de suspensión</dt>
              <dd>{qr.suspension.reason}</dd>
            </>
          )}
          {qr.retired?.reason && (
            <>
              <dt className="text-xs text-ink-muted">Motivo de baja</dt>
              <dd>{qr.retired.reason} · {formatDate(qr.retired.at)}</dd>
            </>
          )}
        </dl>
      </div>

      <VersionsPanel qr={qr} canManage={perms.createQr && !retired} />

      {!retired && (
        <div className="flex flex-wrap gap-2">
          {perms.editDestination && (qr.state === 'activado' || qr.state === 'suspendido') && (
            <Button onClick={() => setModal('destination')}>Editar destino</Button>
          )}
          {perms.manageLifecycle && can('suspend') && qr.state === 'activado' && <Button variant="secondary" onClick={() => setModal('suspend')}>Suspender</Button>}
          {perms.manageLifecycle && can('reactivate') && qr.state === 'suspendido' && !qr.suspension?.security && <Button variant="secondary" onClick={() => setModal('reactivate')}>Reactivar</Button>}
          {perms.manageLifecycle && can('deactivate') && qr.state === 'activado' && qr.kind === 'physical' && <Button variant="secondary" onClick={() => setModal('deactivate')}>Desactivar y reasignar</Button>}
          {perms.manageLifecycle && can('retire') && (
            <Button variant="secondary" className="!border-error text-error-text" onClick={() => setModal('retire')}>Dar de baja</Button>
          )}
        </div>
      )}

      <div className="space-y-2">
        <h2 className="font-display text-base font-bold text-ink">Historial de destinos</h2>
        <Table<DestinationChange>
          caption="Historial de destinos"
          rows={history}
          rowKey={(h) => h.id ?? `${h.at}-${h.after}`}
          empty="Sin cambios de destino."
          columns={[
            { key: 'at', header: 'Fecha', render: (h) => formatDate(h.at) },
            { key: 'by', header: 'Autor', render: actorName },
            { key: 'before', header: 'Antes', render: (h) => <span className="break-all text-xs">{h.before ?? '—'}</span> },
            { key: 'after', header: 'Después', render: (h) => <span className="break-all text-xs">{h.after ?? '—'}</span> },
          ]}
        />
      </div>

      <div className="space-y-2">
        <h2 className="font-display text-base font-bold text-ink">Últimos movimientos de este código</h2>
        <QrTimeline code={qr.code} orgId={client.orgId} limit={8} compact />
      </div>

      {modal === 'destination' && <EditDestinationModal qr={qr} open onClose={() => setModal(null)} onSaved={done('Destino actualizado. Aplica desde el próximo escaneo.')} />}
      {(modal === 'suspend' || modal === 'reactivate' || modal === 'deactivate' || modal === 'retire') && (
        <LifecycleModal
          action={modal}
          qr={qr}
          onClose={() => setModal(null)}
          onDone={done({ suspend: 'QR suspendido.', reactivate: 'QR reactivado.', deactivate: 'QR de vuelta en custodia.', retire: 'QR dado de baja.' }[modal])}
        />
      )}
      <KitModal open={modal === 'kit'} onClose={() => setModal(null)} codes={[qr.code]} domain={qr.domain} style={qr.qrStyle} />
    </section>
  );
}
